import {useCallback, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent} from 'react'

const HOVER_DELAY_MS = 200

// Chat and session rows share one active card, including across sidebar groups.
let activeCard: {owner: object; dismiss: () => void} | null = null

// Repository cards keep their existing controller but participate in the same
// sidebar exclusivity rule as chat and session cards.
export function useSidebarHovercardExclusivity(open: boolean, dismiss: () => void) {
  useEffect(() => {
    if (!open) return
    const entry = {owner: {}, dismiss}
    activeCard?.dismiss()
    activeCard = entry
    return () => {
      if (activeCard === entry) activeCard = null
    }
  }, [open, dismiss])
}

export function useSidebarItemHovercard(onOpenChange?: (open: boolean) => void) {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const onOpenChangeRef = useRef(onOpenChange)
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange
  }, [onOpenChange])
  const anchorRef = useRef<HTMLElement>(null)
  const actionRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const noReturnFocusRef = useRef<HTMLElement>(null)
  const hoveredRegionRef = useRef<'anchor' | 'content' | null>(null)
  const focusedRef = useRef(false)
  const actionMenuOpenRef = useRef(false)
  const restoringFocusRef = useRef(false)
  const restoreFocusRef = useRef(false)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelPendingOpen = useCallback(() => {
    if (openTimerRef.current !== null) clearTimeout(openTimerRef.current)
    openTimerRef.current = null
  }, [])

  const cancelPendingClose = useCallback(() => {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  // NavList's LinkItem replaces its link when it renders, including when a
  // sibling card changes. Preserve focus only across that element replacement.
  const setAnchorRef = useCallback((node: HTMLElement | null) => {
    if (!node) return
    anchorRef.current = node
    if (restoreFocusRef.current && document.activeElement === document.body) {
      restoringFocusRef.current = true
      node.focus()
      restoringFocusRef.current = false
    }
    restoreFocusRef.current = false
    return () => {
      restoreFocusRef.current = document.activeElement === node
      anchorRef.current = null
    }
  }, [])

  const closeNow = useCallback(() => {
    cancelPendingOpen()
    cancelPendingClose()
    if (hoveredRegionRef.current === 'content') hoveredRegionRef.current = null
    if (openRef.current) {
      openRef.current = false
      onOpenChangeRef.current?.(false)
      setOpen(false)
    }
    if (activeCard?.owner === anchorRef) activeCard = null
  }, [cancelPendingOpen, cancelPendingClose])

  const resetInteractionState = useCallback(() => {
    hoveredRegionRef.current = null
    focusedRef.current = false
    restoreFocusRef.current = false
    closeNow()
  }, [closeNow])

  const openNow = useCallback(() => {
    cancelPendingOpen()
    cancelPendingClose()
    if (openRef.current || actionMenuOpenRef.current) return
    activeCard?.dismiss()
    activeCard = {owner: anchorRef, dismiss: closeNow}
    openRef.current = true
    onOpenChangeRef.current?.(true)
    setOpen(true)
  }, [cancelPendingOpen, cancelPendingClose, closeNow])

  const onActionMenuOpenChange = useCallback(
    (isOpen: boolean) => {
      actionMenuOpenRef.current = isOpen
      if (isOpen) closeNow()
    },
    [closeNow],
  )

  const getRegion = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Node)) return null
    if (anchorRef.current?.contains(target) || actionRef.current?.contains(target)) return 'anchor'
    if (contentRef.current?.contains(target)) return 'content'
    return null
  }, [])

  const closeIfInactive = useCallback(() => {
    cancelPendingClose()
    if (hoveredRegionRef.current || focusedRef.current) return
    cancelPendingOpen()
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      closeNow()
    }, HOVER_DELAY_MS)
  }, [cancelPendingClose, cancelPendingOpen, closeNow])

  const onPointerEnter = useCallback(() => {
    const wasHovered = hoveredRegionRef.current === 'anchor'
    hoveredRegionRef.current = 'anchor'
    cancelPendingClose()
    // Replacing a hovered NavList link can fire another enter after dismissal.
    if (wasHovered || openRef.current || openTimerRef.current !== null) return
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      openNow()
    }, HOVER_DELAY_MS)
  }, [cancelPendingClose, openNow])

  const onPointerLeave = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      hoveredRegionRef.current = getRegion(event.relatedTarget)
      closeIfInactive()
    },
    [getRegion, closeIfInactive],
  )

  const onFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (restoringFocusRef.current || !event.currentTarget.matches(':focus-visible')) return
      focusedRef.current = true
      openNow()
    },
    [openNow],
  )

  const onBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      restoreFocusRef.current = false
      focusedRef.current = getRegion(event.relatedTarget) !== null
      closeIfInactive()
    },
    [getRegion, closeIfInactive],
  )

  useEffect(
    () => () => {
      cancelPendingOpen()
      cancelPendingClose()
      if (activeCard?.owner === anchorRef) activeCard = null
    },
    [cancelPendingOpen, cancelPendingClose, closeNow],
  )

  return {
    open,
    anchorRef,
    setAnchorNode: setAnchorRef,
    anchorRegionProps: {ref: setAnchorRef, onPointerEnter, onPointerLeave},
    anchorProps: {onFocus, onBlur},
    actionRef,
    onActionMenuOpenChange,
    contentProps: {
      ref: contentRef,
      onPointerEnter: () => {
        hoveredRegionRef.current = 'content'
        cancelPendingClose()
      },
      onPointerLeave,
    },
    noReturnFocusRef,
    cancelPendingOpen,
    closeNow,
    resetInteractionState,
  }
}
